/**
 * Корзина в localStorage. Никаких таблиц carts/cart_items на сервере (решение #3).
 *
 * Ключ хранения: `stok:cart:v1`.
 * Формат: { items: [{ product_id, name, price, qty, image }, ...] }.
 */
"use client";

import { useSyncExternalStore } from "react";

const KEY = "stok:cart:v1";

export interface CartItem {
  product_id: number;
  external_id: string;
  name: string;
  price: string; // как с бэка (Decimal-string)
  qty: number;
  image?: string | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();

// 30 дней — после этого считаем что цены/наличие устарели и сбрасываем корзину.
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

function read(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return [];
    // TTL: если корзина старше 30 дней — считаем что цены устарели.
    if (typeof parsed.ts === "number" && Date.now() - parsed.ts > TTL_MS) {
      window.localStorage.removeItem(KEY);
      return [];
    }
    return parsed.items;
  } catch {
    return [];
  }
}

function write(items: CartItem[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ items, ts: Date.now() }));
  } catch {
    // ignore (private mode etc.)
  }
}

function emit(): void {
  for (const l of listeners) l();
}

// Подписка на storage-события (синхронизация между вкладками)
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) emit();
  });
}

// Геттер для useSyncExternalStore. Стабилизируем по строке, чтобы snapshot не менялся между вызовами.
let cachedSnapshot: string = JSON.stringify({ items: [] });
let cachedItems: CartItem[] = [];

function getSnapshot(): CartItem[] {
  const items = read();
  const str = JSON.stringify({ items });
  if (str !== cachedSnapshot) {
    cachedSnapshot = str;
    cachedItems = items;
  }
  return cachedItems;
}
function getServerSnapshot(): CartItem[] {
  return [];
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export const cart = {
  add(item: CartItem): void {
    const items = read();
    const existing = items.find((it) => it.product_id === item.product_id);
    if (existing) {
      existing.qty = Math.min(999, existing.qty + item.qty);
    } else {
      items.push({ ...item });
    }
    write(items);
    emit();
  },
  setQty(product_id: number, qty: number): void {
    const items = read();
    const it = items.find((x) => x.product_id === product_id);
    if (!it) return;
    if (qty <= 0) {
      write(items.filter((x) => x.product_id !== product_id));
    } else {
      it.qty = Math.min(999, qty);
      write(items);
    }
    emit();
  },
  remove(product_id: number): void {
    write(read().filter((x) => x.product_id !== product_id));
    emit();
  },
  clear(): void {
    write([]);
    emit();
  },
  total(items: CartItem[]): number {
    return items.reduce((sum, it) => sum + parseFloat(it.price) * it.qty, 0);
  },
};

// Хук-обёртка. Принимает селектор (zustand-стиль).
export function useCart<T = CartItem[]>(selector?: (state: { items: CartItem[] }) => T): T {
  const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  if (!selector) return items as unknown as T;
  return selector({ items });
}
